import { Reveal, Stagger, StaggerItem, HankoStamp, fadeInUp, scaleIn } from './motion';
import { AGENTS, CONNECTORS, useLang, pick } from './content';

const AGENT_GLYPH: Record<string, string> = {
  search: '⌕', compose: '✎', brief: '▤', compare: '⇆', audit: '◉', workflow: '⛓',
};

export function AgentsGrid() {
  const lang = useLang();
  return (
    <section className="border-t border-ink-100 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-16 md:py-20">
        <Reveal>
          <h2 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-ink-900 text-balance max-w-2xl">
            {pick('landing.agents.h2', lang)}
          </h2>
        </Reveal>
        <Stagger stagger={0.07} className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {AGENTS.map((a) => (
            <StaggerItem key={a.key} variant={fadeInUp}>
              <div className="card-paper p-5 h-full flex gap-3">
                <span className="shrink-0 h-9 w-9 rounded-xl bg-accent-50 text-accent-700 inline-flex items-center justify-center text-[16px]">
                  {AGENT_GLYPH[a.key]}
                </span>
                <div>
                  <h3 className="display text-[16px] font-semibold text-ink-900">{pick(a.name, lang)}</h3>
                  <p className="mt-1 text-[13px] text-ink-600 leading-relaxed">{pick(a.body, lang)}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

export function ConnectorsGrid() {
  const lang = useLang();
  return (
    <section className="border-t border-ink-100 bg-paper-50/40">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-16 md:py-20">
        <Reveal>
          <div className="max-w-2xl">
            <div className="text-[12px] uppercase tracking-[0.18em] text-accent-600 font-semibold">{pick(CONNECTORS.eyebrow, lang)}</div>
            <h2 className="mt-2 display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-ink-900 text-balance">
              {pick(CONNECTORS.h2, lang)}
            </h2>
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2 card-paper p-5">
            <div className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold mb-3">{pick(CONNECTORS.globalLabel, lang)}</div>
            <Stagger stagger={0.035} className="flex flex-wrap gap-2">
              {CONNECTORS.global.map((c) => (
                <StaggerItem key={c} variant={scaleIn}>
                  <span className="rounded-lg bg-paper-50 border border-ink-100 px-3 py-1.5 text-[13px] text-ink-700">{c}</span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
          <div className="card-paper p-5 ring-1 ring-accent-500/30 bg-accent-50/30 relative">
            <HankoStamp size={40} rotate={-8} className="absolute top-3 right-3" />
            <div className="text-[11px] uppercase tracking-[0.14em] text-accent-700 font-semibold mb-3">{pick(CONNECTORS.jpLabel, lang)}</div>
            <Stagger stagger={0.05} className="flex flex-wrap gap-2">
              {CONNECTORS.jp.map((c) => (
                <StaggerItem key={c} variant={scaleIn}>
                  <span className="rounded-lg bg-white border border-accent-200 px-3 py-1.5 text-[13px] font-medium text-ink-800">{c}</span>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
        <p className="mt-4 text-[11.5px] text-ink-400">{pick(CONNECTORS.footnote, lang)}</p>
      </div>
    </section>
  );
}
